import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, PoundSterling, Zap, Smartphone, ShieldCheck, Clock, Sun, Wifi, Bell, Plug } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import BundleCTA from "@/components/site/BundleCTA";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";

const features = [
  { icon: PoundSterling, title: "Lower card machine fees", desc: "Sharper rates and transparent pricing — no hidden line items lurking on your statement." },
  { icon: Zap, title: "Faster, more reliable payments", desc: "Next-day settlement and rock-solid uptime so cash flow stays predictable." },
  { icon: Smartphone, title: "Modern, easy-to-use terminals", desc: "Sleek touchscreen devices, contactless, Apple Pay, Google Pay — all built in." },
  { icon: ShieldCheck, title: "Secure by default", desc: "PCI compliant hardware with enterprise-grade encryption baked in." },
];

export default function CardPayments() {
  useSEO({
    title: "Card Payment Solutions — Lower fees, faster payouts",
    description: "Cut card machine costs and upgrade to modern terminals. We work with the right partner for your business — next-day settlement, EPOS integrations and a local team.",
    path: "/card-payments",
  });
  return (
    <div data-testid="card-payments-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Card payment solutions</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-5">
                Lower fees.<br />Faster payments.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                We work with the <strong className="text-gray-900">right partner for your business</strong> — helping you cut card machine costs and upgrade to terminals customers actually enjoy paying with.
              </p>

              {/* Key selling points */}
              <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl" data-testid="payments-selling-points">
                {[
                  "Lower card machine fees",
                  "Next-day settlement available",
                  "Modern, reliable hardware",
                  "Works with your EPOS system",
                  "No jargon, honest advice",
                  "Local Northern Ireland team",
                ].map((p) => (
                  <div key={p} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-700">{p}</span>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="payments-cta-comparison"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-200 shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  Request a Cost Comparison <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-8 bg-[#FF5A1F]/15 blur-3xl rounded-full" />
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                  <img
                    src={IMG.cardInUseCafe}
                    alt="Café staff member taking a contactless payment from a customer"
                    className="w-full h-full object-cover"
                    data-testid="payments-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
              Everything you need at the till — nothing you don't.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`payments-feature-${i}`}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                  <f.icon className="text-[#FF5A1F]" size={22} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3 text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS WE SET UP */}
      <section className="py-24 md:py-32 bg-white" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Solutions we set up</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              The right kit for the way you trade.
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              Whether you're behind a counter, taking orders at the table, or trading outdoors — we'll spec, source and set up a solution that fits. No pushing you towards one provider.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                key: "countertop",
                name: "Countertop",
                tag: "Till-side",
                desc: "Stationary, always-on payments — plugs into your till or EPOS.",
                icon: Plug,
                features: ["EPOS integrated", "Always powered", "Reliable at the till"],
              },
              {
                key: "portable",
                name: "Portable",
                tag: "All-round",
                desc: "Wireless, everywhere-in-the-shop terminal built for high-pace service.",
                icon: Smartphone,
                features: ["4G + Wi-Fi", "Tap, chip, swipe", "All-in-one"],
              },
              {
                key: "mobile",
                name: "Mobile / At-table",
                tag: "On the move",
                desc: "Take orders and payments at the table or on the floor.",
                icon: Zap,
                features: ["Pocket-sized", "Built-in printer", "Order at table"],
              },
              {
                key: "outdoor",
                name: "Outdoor & Pop-up",
                tag: "Trade anywhere",
                desc: "Rugged, weather-ready solutions for markets, events and forecourts.",
                icon: Sun,
                features: ["Weather-resilient", "Long battery", "4G connectivity"],
              },
            ].map((t) => (
              <div
                key={t.key}
                data-testid={`solution-${t.key}`}
                className="group relative bg-[#F8F9FA] rounded-3xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="absolute top-4 right-4 z-10">
                  <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF5A1F] rounded-full border border-orange-100">
                    {t.tag}
                  </span>
                </div>
                <div className="aspect-square bg-gradient-to-br from-white to-gray-50 flex items-center justify-center p-8">
                  <div className="w-24 h-24 rounded-3xl bg-orange-50 flex items-center justify-center border border-orange-100 group-hover:scale-110 transition-transform duration-500">
                    <t.icon size={40} className="text-[#FF5A1F]" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-6 pt-5 border-t border-gray-100">
                  <h3 className="font-display text-xl font-bold text-gray-900">{t.name}</h3>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{t.desc}</p>
                  <ul className="mt-4 space-y-1.5">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                        <span className="w-1 h-1 rounded-full bg-[#FF5A1F]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="solutions-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/25"
            >
              Help me pick the right setup <ArrowRight size={18} />
            </Link>
            <p className="text-sm text-gray-500">We'll match the kit — and the provider — to how your business actually works.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <WaveBackground />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">How it works</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
                Switching is simpler than you think.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Send us a recent statement, we'll benchmark it against what we can offer, and you'll see — in pounds and pence — what switching saves you each month.
              </p>
            </div>

            <div className="space-y-5">
              {[
                ["01", "Share a statement", "Upload a recent merchant services statement. We do the rest."],
                ["02", "Get a like-for-like comparison", "A clear breakdown of every fee, side-by-side."],
                ["03", "Switch in days, not weeks", "Hardware shipped and configured. We're with you on go-live day."],
              ].map(([n, t, d]) => (
                <div key={n} className="flex gap-5 p-6 bg-white border border-gray-100 rounded-2xl hover:border-gray-200 transition">
                  <div className="font-display text-3xl font-extrabold text-[#FF5A1F]">{n}</div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-gray-900">{t}</h4>
                    <p className="text-gray-600 mt-1">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEXT-DAY PAYOUTS */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]" data-testid="next-day-payouts-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100">
                <Sun size={14} className="text-[#FF5A1F]" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">7 days a week — including weekends</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-5">
                Get paid the next day.<br />Every day<span className="text-[#FF5A1F]">.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                With the right provider on your side, every card transaction settles into your business bank account the very next day — typically by 10–11am. That includes Saturdays, Sundays and bank holidays. No more waiting three working days to see your money.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-5">
                {[
                  ["10am", "Funds in by"],
                  ["7 days", "A week"],
                  ["£0", "Settlement fees"],
                ].map(([n, l]) => (
                  <div key={l} className="bg-white border border-gray-100 rounded-2xl p-5">
                    <p className="font-display text-3xl font-extrabold text-gray-900">{n}</p>
                    <p className="text-sm text-gray-500 mt-1">{l}</p>
                  </div>
                ))}
              </div>

              <ul className="mt-8 space-y-3 text-gray-700">
                {[
                  "No upgrades needed — included on every plan",
                  "Cash flow that matches the pace of your business",
                  "Track every payout from your provider's app or web portal",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0" />
                    <span className="font-medium">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="relative bg-white rounded-3xl border border-gray-100 p-8 shadow-xl">
                <div className="absolute -inset-6 bg-[#FF5A1F]/10 blur-3xl rounded-full -z-10" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500">Recent payout</p>
                <p className="font-display text-4xl font-extrabold text-gray-900 mt-2">£3,847.20</p>
                <p className="text-sm text-gray-500 mt-1">Settled · Today, 10:42am</p>

                <div className="mt-6 space-y-3">
                  {[
                    ["Sun · 28 transactions", "+£842.10"],
                    ["Sat · 41 transactions", "+£1,520.50"],
                    ["Fri · 39 transactions", "+£1,484.60"],
                  ].map(([d, a]) => (
                    <div key={d} className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-xl">
                      <span className="text-sm text-gray-700 font-medium">{d}</span>
                      <span className="text-sm font-bold text-[#FF5A1F]">{a}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
                  <Bell size={14} className="text-[#FF5A1F]" />
                  <span>Push notifications when funds arrive</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EPOS INTEGRATIONS */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden" data-testid="epos-integrations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-8 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
                <div className="relative grid grid-cols-3 gap-3">
                  {[
                    "Square", "Lightspeed", "Toast", "Tevalis", "Zonal", "ICRTouch",
                    "Vita Mojo", "Aireus", "TISSL", "Goodtill", "Epos Now", "Comtrex",
                  ].map((name, i) => (
                    <div
                      key={name}
                      className={`aspect-square rounded-2xl border flex items-center justify-center text-center px-2 transition hover:-translate-y-1 ${
                        i % 4 === 0
                          ? "bg-[#0a0a0a] text-white border-gray-900"
                          : "bg-white text-gray-700 border-gray-100"
                      }`}
                    >
                      <span className="font-display text-xs md:text-sm font-bold tracking-tight">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF5A1F] text-white text-xs font-bold uppercase tracking-[0.18em] rounded-full">
                    <Plug size={14} />
                    + many more
                  </span>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">EPOS integrations</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
                Plays nicely with your<br />till system.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Whatever you use behind the counter, chances are we can integrate with it. We work with payment providers that connect to <strong className="text-gray-900">all the major EPOS systems</strong> — from hospitality giants to local favourites — so totals fly straight from the till to the terminal. No re-keying, no mistakes, no awkward end-of-day reconciliation.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                {[
                  { icon: Plug, title: "Major EPOS supported", desc: "Square, Lightspeed, Toast, Tevalis, Zonal, ICRTouch and many more." },
                  { icon: Wifi, title: "Reliable uptime", desc: "Built-in 4G fail-over keeps payments flowing if Wi-Fi drops." },
                  { icon: Zap, title: "Fast payments", desc: "Modern terminals designed for high-pace, face-to-face service." },
                  { icon: ShieldCheck, title: "End-to-end encryption", desc: "Point-to-point encryption and instant fraud detection." },
                ].map((it, i) => (
                  <div key={it.title} data-testid={`epos-perk-${i}`} className="p-5 bg-[#F8F9FA] rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <it.icon size={18} className="text-[#FF5A1F]" />
                      <h4 className="font-display text-base font-bold text-gray-900">{it.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{it.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES SERVED */}
      <section className="py-24 md:py-32 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Payments tailored for your industry</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              Built for the way you trade.
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              We match you with payment tech designed specifically for your sector — not a one-size-fits-all box.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { title: "Quick-service restaurants", desc: "Power through queues, faster." },
              { title: "Full-service restaurants", desc: "EPOS-integrated billing." },
              { title: "Cafés & coffee shops", desc: "Tap, sip, repeat." },
              { title: "Pubs & bars", desc: "Split bills with ease." },
              { title: "Retail & shops", desc: "Counter, mobile or kiosk." },
              { title: "Dental & beauty", desc: "Easy reconciliation." },
              { title: "Pharmacies", desc: "Trusted, reliable till." },
              { title: "Salons & barbers", desc: "Take payments anywhere." },
              { title: "Takeaways", desc: "Smart delivery payments." },
              { title: "Off-licences", desc: "Speed at the counter." },
              { title: "Garages", desc: "Forecourt-ready terminals." },
              { title: "Pop-ups & events", desc: "Always-on 4G payments." },
            ].map((it, i) => (
              <div
                key={it.title}
                data-testid={`industry-${i}`}
                className="bg-[#F8F9FA] rounded-2xl p-5 border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <h4 className="font-display text-sm md:text-base font-bold text-gray-900 leading-tight">{it.title}</h4>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="py-20 bg-white" data-testid="faqs-teaser-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">FAQs</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mt-3">
            Got questions? We've got answers.
          </h2>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Fees, payouts, EPOS, contracts, PCI — the things business owners ask us most are all collected in one place.
          </p>
          <Link
            to="/faq"
            data-testid="payments-faq-link"
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full transition-all hover:-translate-y-0.5"
          >
            Read the FAQs <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* BUNDLE CROSS-SELL */}
      <BundleCTA />

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Clock className="mx-auto text-[#FF5A1F] mb-6" size={36} />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Five-minute quote.<br />
            Could save you thousands<span className="text-[#FF5A1F]">.</span>
          </h2>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
            Most clients are surprised by how much they're overpaying. Let's find out what you could be saving.
          </p>
          <Link
            to="/contact"
            data-testid="payments-final-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/30"
          >
            Request a Cost Comparison <ArrowRight size={18} />
          </Link>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-400">
            {["No obligation", "Free benchmark", "UK-based support"].map((t) => (
              <span key={t} className="flex items-center gap-2 font-medium">
                <CheckCircle2 size={16} className="text-[#FF5A1F]" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

